"use client";

import {
  ContainerBroadcastEvent,
  MessageType,
  useBridge,
} from "@/src/lib/bridge";

import { MEMBER_INVALIDATE_QUERY_ROOTS } from "@/src/utils/memberInvalidateQueries";
import { QUERY_KEYS } from "@/src/config";
import { getContainerRouteContext } from "@/src/lib/bridge/containerRouteMeta";
import log from "logbeacon";
import { throttle } from "lodash-es";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

/** 原生中继后的 `container-broadcast` data 形状（与 iOS/Android envelope.data 对齐） */
export interface ContainerBroadcastPayload {
  event: string;
  ts?: number;
  relayedByNative?: boolean;
  payload?: unknown;
  sourceTabIndex?: number;
  sourceContainerId?: string;
}

/**
 * 将广播事件名映射到需失效的 Query「根 key」（与业务 queryKey 第一段一致）。
 * 未知事件采用保守的会员域刷新，避免多 Tab 登录态长期分叉。
 */
export function queryRootsForContainerBroadcastEvent(event: string): string[] {
  const e = (event || "").toLowerCase();
  if (e === ContainerBroadcastEvent.MEMBER_UPDATED) {
    return [...MEMBER_INVALIDATE_QUERY_ROOTS];
  }
  if (e === ContainerBroadcastEvent.TRACKER_READ_WATERMARK_UPDATED) {
    return [QUERY_KEYS.TRACKER_LIST, QUERY_KEYS.TRACKER_DETAIL];
  }
  if (
    e.includes("member") ||
    e.includes("plan") ||
    e.includes("equity") ||
    e === "subscription"
  ) {
    return [
      QUERY_KEYS.MEMBER_FEATURE,
      QUERY_KEYS.MEMBER_USE_PLAN,
      QUERY_KEYS.MEMBER_FEATURE_EQUITYLIST,
      QUERY_KEYS.MEMBER_EQUITY_LIST,
    ];
  }
  if (e.includes("portfolio") || e.includes("position")) {
    return [
      QUERY_KEYS.PORTFOLIO_ASSETS_INFINITE,
      QUERY_KEYS.PORTFOLIO_ASSETS,
      QUERY_KEYS.PORTFOLIO_PERFORMANCE,
    ];
  }
  if (e.includes("chat") || e.includes("conversation")) {
    return [QUERY_KEYS.CHAT_HISTORY];
  }
  if (e.includes("bind") || e.includes("owner")) {
    return [QUERY_KEYS.GET_OWNER_BY_UNIQUE_KEY, QUERY_KEYS.GET_BIND];
  }
  if (e.includes("favorite") || e.includes("collect")) {
    return [QUERY_KEYS.PORTFOLIO_ASSETS_INFINITE];
  }
  return [QUERY_KEYS.MEMBER_FEATURE];
}

/**
 * 监听 App 下发的 `container-broadcast`，节流后按事件类型 invalidate React Query。
 * 用于多 WebView 下非活跃 Tab 与广播源的数据一致性（混合架构 StateSync）。
 */
export function useContainerBroadcastStateSync() {
  const { on, isInApp } = useBridge();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isInApp || !on) {
      return;
    }

    const flush = (data: ContainerBroadcastPayload) => {
      const self = getContainerRouteContext();
      const src = data.sourceTabIndex;
      if (
        typeof src === "number" &&
        src >= 0 &&
        self.tabIndex != null &&
        src === self.tabIndex
      ) {
        return;
      }
      const roots = queryRootsForContainerBroadcastEvent(data.event);
      for (const key of roots) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
      log.debug("[ContainerBroadcastStateSync] invalidateQueries", {
        event: data.event,
        sourceTabIndex: src,
        selfTab: self.tabIndex,
        roots,
      });
    };

    const throttled = throttle(flush, 400, { leading: true, trailing: true });

    const unsubscribe = on(
      MessageType.CONTAINER_BROADCAST,
      (data: ContainerBroadcastPayload) => {
        if (!data?.event) {
          return;
        }
        throttled(data);
      },
    );

    return () => {
      unsubscribe();
      throttled.cancel();
    };
  }, [isInApp, on, queryClient]);
}
